<script setup>
import { computed, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { PERMISSION_GROUPS } from 'dashboard/constants/permissions.js';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  role: {
    type: Object,
    default: () => ({}),
  },
  submitLabel: {
    type: String,
    required: true,
  },
  isSubmitting: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['submit', 'cancel']);

const { t } = useI18n();

const form = reactive({
  name: props.role?.name || '',
  description: props.role?.description || '',
});

const selectedPermissions = ref(new Set(props.role?.permissions || []));

watch(
  () => props.role,
  newRole => {
    form.name = newRole?.name || '';
    form.description = newRole?.description || '';
    selectedPermissions.value = new Set(newRole?.permissions || []);
  }
);

const nameError = computed(() => {
  if (!form.name.trim()) {
    return t('SETTINGS.CUSTOM_ROLES.FORM.NAME.ERROR');
  }
  return '';
});

const isValid = computed(() => !nameError.value);

const togglePermission = key => {
  if (selectedPermissions.value.has(key)) {
    selectedPermissions.value.delete(key);
  } else {
    selectedPermissions.value.add(key);
  }
  // trigger reactivity
  selectedPermissions.value = new Set(selectedPermissions.value);
};

const toggleGroup = group => {
  const groupKeys = group.permissions.map(p => p.key);
  const allSelected = groupKeys.every(k => selectedPermissions.value.has(k));
  if (allSelected) {
    groupKeys.forEach(k => selectedPermissions.value.delete(k));
  } else {
    groupKeys.forEach(k => selectedPermissions.value.add(k));
  }
  selectedPermissions.value = new Set(selectedPermissions.value);
};

const isGroupAllSelected = group =>
  group.permissions.every(p => selectedPermissions.value.has(p.key));

const onSubmit = () => {
  if (!isValid.value) return;
  emit('submit', {
    name: form.name.trim(),
    description: form.description.trim(),
    permissions: Array.from(selectedPermissions.value),
  });
};
</script>

<template>
  <form class="flex flex-col gap-6" @submit.prevent="onSubmit">
    <div class="flex flex-col gap-2">
      <label class="text-sm font-medium text-n-slate-12" for="role-name">
        {{ t('SETTINGS.CUSTOM_ROLES.FORM.NAME.LABEL') }}
      </label>
      <input
        id="role-name"
        v-model="form.name"
        type="text"
        class="w-full rounded-lg border border-n-weak bg-n-alpha-black2 px-3 py-2 text-sm text-n-slate-12 focus:outline-none focus:border-n-brand"
        :placeholder="t('SETTINGS.CUSTOM_ROLES.FORM.NAME.PLACEHOLDER')"
      />
      <span v-if="nameError" class="text-xs text-n-ruby-9">{{ nameError }}</span>
    </div>

    <div class="flex flex-col gap-2">
      <label class="text-sm font-medium text-n-slate-12" for="role-description">
        {{ t('SETTINGS.CUSTOM_ROLES.FORM.DESCRIPTION.LABEL') }}
      </label>
      <textarea
        id="role-description"
        v-model="form.description"
        rows="3"
        class="w-full rounded-lg border border-n-weak bg-n-alpha-black2 px-3 py-2 text-sm text-n-slate-12 focus:outline-none focus:border-n-brand"
        :placeholder="t('SETTINGS.CUSTOM_ROLES.FORM.DESCRIPTION.PLACEHOLDER')"
      />
    </div>

    <div class="flex flex-col gap-3">
      <label class="text-sm font-medium text-n-slate-12">
        {{ t('SETTINGS.CUSTOM_ROLES.FORM.PERMISSIONS.LABEL') }}
      </label>
      <div class="flex flex-col gap-4 rounded-lg border border-n-weak p-4">
        <div
          v-for="group in PERMISSION_GROUPS"
          :key="group.key"
          class="flex flex-col gap-2"
        >
          <label class="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              :checked="isGroupAllSelected(group)"
              class="h-4 w-4 rounded border-n-weak"
              @change="toggleGroup(group)"
            />
            <span class="text-sm font-semibold text-n-slate-12">
              {{ t(group.label) }}
            </span>
          </label>
          <div class="flex flex-col gap-2 pl-6">
            <label
              v-for="perm in group.permissions"
              :key="perm.key"
              class="flex items-center gap-2 cursor-pointer"
            >
              <input
                type="checkbox"
                :checked="selectedPermissions.has(perm.key)"
                class="h-4 w-4 rounded border-n-weak"
                @change="togglePermission(perm.key)"
              />
              <span class="text-sm text-n-slate-11">{{ t(perm.label) }}</span>
            </label>
          </div>
        </div>
      </div>
    </div>

    <div class="flex items-center justify-end gap-2">
      <Button
        faded
        slate
        type="button"
        :label="t('SETTINGS.CUSTOM_ROLES.FORM.CANCEL')"
        @click="emit('cancel')"
      />
      <Button
        type="submit"
        :label="submitLabel"
        :disabled="!isValid || isSubmitting"
        :is-loading="isSubmitting"
      />
    </div>
  </form>
</template>
